import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ServiceTypeDrawer, type ServiceType } from '../features/ServiceTypeDrawer'
import { Badge } from '../components/ui'
import { ArrowDown, ArrowUp, Download, LogOut, Pencil, Plus, Volume2, Vibrate } from 'lucide-react'
import { api, type Me } from '../lib/api'
import { rm } from '../lib/format'
import { Button, Card, Field, Input, Skeleton, cx, useToast } from '../components/ui'
import {
  feedback,
  hapticEnabled,
  setHapticEnabled,
  setSoundEnabled,
  soundEnabled,
} from '../lib/feedback'

interface SettingsData {
  settings: {
    shopName: string
    shopNameMs: string
    shopNameEn: string
    addressLine: string
    phoneE164: string
    whatsappE164: string
    mapsUrl: string
    businessHours: Record<string, { open: string; close: string } | null>
  }
  priceItems: Array<{ id: string; nameZh: string; defaultAmount: number }>
  holidays: Array<{ id: string; date: string; name: string; isClosed: boolean }>
}

const DOW = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

export function SettingsPage({ me }: { me: Me }) {
  const qc = useQueryClient()
  const toast = useToast()
  const isOwner = me.user.role === 'OWNER'

  const q = useQuery({ queryKey: ['settings'], queryFn: () => api.get<SettingsData>('/api/settings') })

  const [form, setForm] = useState<Record<string, string>>({})
  // 存本机不是账号：柜台平板可能要静音，自己手机上可能想要
  const [sound, setSound] = useState(soundEnabled)
  const [haptic, setHaptic] = useState(hapticEnabled)
  const [svcOpen, setSvcOpen] = useState(false)
  const [svcEditing, setSvcEditing] = useState<ServiceType | null>(null)

  const svcQ = useQuery({
    queryKey: ['service-types'],
    queryFn: () => api.get<{ items: ServiceType[] }>('/api/service-types?all=true'),
  })

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.post('/api/service-types/reorder', { ids }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['service-types'] }),
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  const moveSvc = (i: number, dir: -1 | 1) => {
    const items = svcQ.data?.items ?? []
    const j = i + dir
    if (j < 0 || j >= items.length) return
    const ids = items.map((s) => s.id)
    ;[ids[i], ids[j]] = [ids[j]!, ids[i]!]
    reorder.mutate(ids)
  }

  useEffect(() => {
    if (q.data) {
      const s = q.data.settings
      setForm({
        shopName: s.shopName,
        shopNameMs: s.shopNameMs,
        shopNameEn: s.shopNameEn,
        addressLine: s.addressLine,
        phoneE164: s.phoneE164,
        whatsappE164: s.whatsappE164,
        mapsUrl: s.mapsUrl,
      })
    }
  }, [q.data])

  const save = useMutation({
    mutationFn: () => api.patch('/api/settings', form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] })
      toast.show('已保存')
    },
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  const logout = async () => {
    await api.post('/api/auth/logout')
    window.location.href = '/login'
  }

  if (q.isLoading) return <Skeleton className="h-96 max-w-[720px]" />

  const hours = q.data?.settings.businessHours ?? {}

  return (
    <div className="mx-auto flex max-w-[720px] flex-col gap-4">
      <h1 className="text-[28px] font-bold">设置</h1>

      <Card title="店铺信息">
        <div className="flex flex-col gap-5">
          <Field label="店名（中文）">
            <Input
              disabled={!isOwner}
              value={form.shopName ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, shopName: e.target.value }))}
            />
          </Field>
          <Field label="店名（Bahasa Melayu）" hint="顾客链接页与 WhatsApp 提醒会用这个">
            <Input
              disabled={!isOwner}
              value={form.shopNameMs ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, shopNameMs: e.target.value }))}
            />
          </Field>
          <Field label="店名（English）">
            <Input
              disabled={!isOwner}
              value={form.shopNameEn ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, shopNameEn: e.target.value }))}
            />
          </Field>
          <Field label="地址">
            <Input
              disabled={!isOwner}
              value={form.addressLine ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, addressLine: e.target.value }))}
            />
          </Field>
          <Field label="Google Maps 链接" hint="顾客链接页的「打开地图」按钮">
            <Input
              disabled={!isOwner}
              value={form.mapsUrl ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, mapsUrl: e.target.value }))}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="店铺电话">
              <Input
                disabled={!isOwner}
                className="tnum"
                value={form.phoneE164 ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, phoneE164: e.target.value }))}
              />
            </Field>
            <Field label="WhatsApp 号码">
              <Input
                disabled={!isOwner}
                className="tnum"
                value={form.whatsappE164 ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, whatsappE164: e.target.value }))}
              />
            </Field>
          </div>

          {isOwner && (
            <Button size="lg" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              保存
            </Button>
          )}
        </div>
      </Card>

      <Card title="营业时间">
        <ul className="flex flex-col gap-2 text-[17px]">
          {DOW.map((d, i) => {
            const h = hours[String(i)]
            return (
              <li key={d} className="flex items-center justify-between border-b border-line pb-2 last:border-0">
                <span>{d}</span>
                <span className={h ? 'tnum' : 'text-ink-3'}>
                  {h ? `${h.open} – ${h.close}` : '休息'}
                </span>
              </li>
            )
          })}
        </ul>
        <p className="mt-3 text-[15px] text-ink-3">
          休息日仍可以手动排入预约，只会给个提示。
        </p>
      </Card>

      <Card
        title="服务与价格"
        action={
          isOwner && (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={16} />}
              onClick={() => {
                setSvcEditing(null)
                setSvcOpen(true)
              }}
            >
              新增
            </Button>
          )
        }
        bodyClassName="p-0! sm:p-0!"
      >
        {svcQ.isLoading ? (
          <div className="p-5">
            <Skeleton className="h-32" />
          </div>
        ) : (
          <ul>
            {(svcQ.data?.items ?? []).map((s, i) => (
              <li
                key={s.id}
                className="flex items-center gap-3 border-b border-line px-5 py-3.5 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-[17px] font-semibold">{s.nameZh}</span>
                    {!s.isActive && <Badge tone="neutral">已停用</Badge>}
                    {s.caseCount > 0 && (
                      <span className="tnum text-[14px] text-ink-3">{s.caseCount} 张工单</span>
                    )}
                  </span>
                  <span className="tnum block text-[15px] text-ink-2">
                    约 {s.leadDays} 天
                    {s.defaultAmount > 0 ? ` · ${rm(s.defaultAmount)}` : ' · 未设默认价'}
                    {' · '}
                    {s.flowSteps.length} 步
                  </span>
                  <span className="block truncate text-[14px] text-ink-3">
                    {s.flowSteps.map((f) => f.title).join(' → ')}
                  </span>
                </div>

                {isOwner && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="上移"
                      icon={<ArrowUp size={17} />}
                      disabled={i === 0 || reorder.isPending}
                      onClick={() => moveSvc(i, -1)}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="下移"
                      icon={<ArrowDown size={17} />}
                      disabled={i === (svcQ.data?.items.length ?? 1) - 1 || reorder.isPending}
                      onClick={() => moveSvc(i, 1)}
                    />
                    <Button
                      size="sm"
                      icon={<Pencil size={16} />}
                      onClick={() => {
                        setSvcEditing(s)
                        setSvcOpen(true)
                      }}
                    >
                      编辑
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="px-5 py-3 text-[15px] text-ink-3">
          顺序就是开单时按钮的排列顺序。最常做的放最前面。
        </p>
      </Card>

      <Card title="旧价目表（不再使用）" className="hidden">
        {(q.data?.priceItems.length ?? 0) === 0 ? (
          <p className="text-[16px] text-ink-3">还没有设置价格</p>
        ) : (
          <ul className="flex flex-col gap-2 text-[17px]">
            {q.data!.priceItems.map((p) => (
              <li key={p.id} className="flex justify-between border-b border-line pb-2 last:border-0">
                <span>{p.nameZh}</span>
                <span className="tnum font-semibold">{rm(p.defaultAmount)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="提示音与振动">
        <p className="mb-4 text-[16px] text-ink-2">
          只在<strong>完成一步</strong>、<strong>收到钱</strong>、<strong>开完单</strong>时响一下，
          翻页和打字不会出声。顾客在旁边觉得吵就关掉 —— 这个设置只影响这台设备。
        </p>

        <label className="flex min-h-14 cursor-pointer items-center gap-3 border-b border-line text-[17px]">
          <Volume2 size={22} className="shrink-0 text-ink-2" />
          <span className="flex-1">提示音</span>
          <input
            type="checkbox"
            checked={sound}
            onChange={(e) => {
              setSound(e.target.checked)
              setSoundEnabled(e.target.checked)
              if (e.target.checked) feedback('done')
            }}
            className="size-6 accent-brand-600"
          />
        </label>

        <label className="flex min-h-14 cursor-pointer items-center gap-3 text-[17px]">
          <Vibrate size={22} className="shrink-0 text-ink-2" />
          <span className="flex-1">
            振动
            <span className="block text-[15px] text-ink-3">手机上有效，平板多半没有</span>
          </span>
          <input
            type="checkbox"
            checked={haptic}
            onChange={(e) => {
              setHaptic(e.target.checked)
              setHapticEnabled(e.target.checked)
              if (e.target.checked) feedback('done')
            }}
            className="size-6 accent-brand-600"
          />
        </label>

        <Button className="mt-4" icon={<Volume2 size={18} />} onClick={() => feedback('money')}>
          试听「收到钱」
        </Button>
      </Card>

      <ServiceTypeDrawer
        open={svcOpen}
        onClose={() => setSvcOpen(false)}
        item={svcEditing}
      />

      {isOwner && <BackupCard />}

      <Card title="账号">
        <p className="text-[17px]">
          {me.user.name} · {me.user.role === 'OWNER' ? '老板' : '助手'}
        </p>
        <Button className="mt-3" icon={<LogOut size={18} />} onClick={() => void logout()}>
          退出登录
        </Button>
      </Card>
    </div>
  )
}

/**
 * 备份状态。
 *
 * 这张卡片存在的唯一理由：备份坏过整整一段时间，没有任何地方看得见。
 * 容器里的 pg_dump 是 15、数据库是 18，版本不匹配导致 pg_dump 直接中止，
 * 每天生成一个 20 字节的空文件，而代码把错误按 warn 吞了。
 *
 * 所以这里【不显示「一切正常」】，只显示最近一份备份有多大、多久以前 ——
 * 大小是唯一能让人一眼看出「这不对」的东西。
 */
function BackupCard() {
  const toast = useToast()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['backups'],
    queryFn: () =>
      api.get<{
        items: Array<{ name: string; bytes: number; createdAt: string }>
        health: { at: string; ok: boolean; bytes: number; error: string | null } | null
      }>('/api/backups'),
  })

  // 「上次取走」和「上次生成」是两回事：服务器天天生成，
  // 但只有下载到别处的那一份，才在 Railway 出事时救得了命。
  const dl = useQuery({
    queryKey: ['backup-status'],
    queryFn: () =>
      api.get<{ lastDownloadedAt: string | null; daysSince: number | null; overdue: boolean }>(
        '/api/backups/status',
      ),
  })

  const run = useMutation({
    mutationFn: () => api.post<{ ok: boolean; bytes: number }>('/api/backups/run'),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['backups'] })
      toast.show(`备份完成，${mb(r.bytes)}`)
    },
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  const latest = q.data?.items[0]
  const health = q.data?.health

  return (
    <Card title="数据备份">
      {q.isLoading ? (
        <Skeleton className="h-16" />
      ) : (
        <>
          {latest ? (
            <p className="tnum text-[17px]">
              最近一份：{latest.name.replace('gigibook-', '').replace('.sql.gz', '')} ·{' '}
              <strong>{mb(latest.bytes)}</strong>
            </p>
          ) : (
            <p className="text-[17px] text-bad">还没有任何备份</p>
          )}

          {health && !health.ok && health.error && (
            <p className="mt-2 rounded-md border border-bad-line bg-bad-bg px-4 py-3 text-[15px] text-bad">
              上次备份失败：{health.error}
            </p>
          )}

          {dl.data && (
            <p
              className={cx(
                'mt-3 rounded-md px-4 py-3 text-[16px]',
                dl.data.overdue
                  ? 'border border-warn-line bg-warn-bg text-warn'
                  : 'bg-sunken text-ink-2',
              )}
            >
              {dl.data.daysSince === null
                ? '还没有把备份下载到自己的电脑过。'
                : dl.data.overdue
                  ? `已经 ${dl.data.daysSince} 天没下载备份了，该下一份了。`
                  : `上次下载到自己电脑：${dl.data.daysSince} 天前。`}
            </p>
          )}

          <p className="mt-2 text-[15px] text-ink-3">
            备份存在服务器上，只防误删，<strong>不防 Railway 账号出问题</strong>。
            每月下载一份存到自己的电脑或 Google Drive。
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button loading={run.isPending} onClick={() => run.mutate()}>
              立刻备份一次
            </Button>
            {latest && (
              /* 数据库只对内网开放，这是老板把备份取到自己电脑的唯一途径 */
              <a
                href={`/api/backups/${latest.name}`}
                download
                onClick={() => {
                  // 让「上次下载」立刻更新，不用等缓存过期
                  setTimeout(() => void qc.invalidateQueries({ queryKey: ['backup-status'] }), 1500)
                }}
                className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong px-4 text-[17px] font-semibold text-ink hover:bg-sunken"
              >
                <Download size={18} />
                下载到我的电脑
              </a>
            )}
          </div>
        </>
      )}
    </Card>
  )
}

function mb(bytes: number): string {
  if (bytes < 1024) return `${bytes} 字节（太小，多半是坏的）`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
